import React from "react";
import { useNavigate } from "react-router-dom";
import C from "../constants/colors";
import { fmtCurrency } from "../utils/format";
import { getClientDisplayName } from "../utils/unitCalculations";

const NavLink = ({ to, icon, children }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.6875rem", fontWeight: 700, color: C.primary, textTransform: "uppercase", letterSpacing: "0.08em" }}
    >
      <i className={icon} />
      {children}
    </button>
  );
};

const FinBox = ({ label, value, valueColor = C.primary }) => (
  <div style={{ flex: 1, padding: "1rem 1.25rem", background: "#f7f2fa", borderRadius: 10 }}>
    <p style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, marginBottom: "0.25rem" }}>
      {label}
    </p>
    <p style={{ margin: 0, fontWeight: 800, color: valueColor, fontSize: "1rem", letterSpacing: "-0.01em" }}>
      {value}
    </p>
  </div>
);

const MetaChip = ({ icon, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.75rem", color: C.muted }}>
    <i className={icon} style={{ color: C.primary, fontSize: "0.875rem" }} />
    {children}
  </span>
);

const ContractOwnership = ({ contract, client, currency }) => {
  const clientName = getClientDisplayName(client);

  const clientInitials = clientName !== "—"
    ? clientName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <div className="ud-section-card h-100">
      {/* ── Header ── */}
      <div className="ud-section-card-header">
        <span style={{ fontWeight: 700, color: C.primary, fontSize: "0.9375rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <i className="ri-file-text-line" /> Contract &amp; Ownership
        </span>
        {contract && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <span style={{ padding: "0.25rem 0.75rem", background: "#f1f5f9", borderRadius: 6, fontSize: "0.6875rem", fontWeight: 700, color: C.primary }}>
              PVC {contract.number}
            </span>
            <NavLink to={`/financial/contracts/${contract.id}`} icon="ri-external-link-line">
              View Contract
            </NavLink>
          </div>
        )}
      </div>

      {contract ? (
        <div style={{ padding: "1.25rem 1.75rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* ── Client strip ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.875rem 1rem", background: "#f7f2fa", borderRadius: 10 }}>
            <div className="ud-client-avatar" style={{ flexShrink: 0 }}>{clientInitials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, color: C.primary, fontSize: "0.9375rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {clientName}
              </p>
              {client?.buyer_type === "LEGAL" && client?.company && (
                <p style={{ margin: 0, fontSize: "0.75rem", color: C.muted }}>{client.company}</p>
              )}
              {client?.buyer_type === "NATURAL" && client?.profession && (
                <p style={{ margin: 0, fontSize: "0.75rem", color: C.muted }}>{client.profession}</p>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.35rem", flexShrink: 0 }}>
              {client?.mobile_phone_number && (
                <MetaChip icon="ri-phone-line">{client.mobile_phone_number}</MetaChip>
              )}
              {(client?.personal_email || client?.business_email) && (
                <MetaChip icon="ri-mail-line">{client.personal_email || client.business_email}</MetaChip>
              )}
            </div>
            {client && (
              <NavLink to={`/client-details/${client.id}`} icon="ri-user-line">
                View Client
              </NavLink>
            )}
          </div>

          {/* ── 3 financial boxes ── */}
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <FinBox
              label="Purchase Price"
              value={fmtCurrency(contract.purchase_price, currency)}
            />
            <FinBox
              label="Extra Finishing"
              value={contract.extra_finishing > 0 ? fmtCurrency(contract.extra_finishing, currency) : "—"}
              valueColor={contract.extra_finishing > 0 ? C.primary : C.muted}
            />
            <FinBox
              label="Grand Total"
              value={fmtCurrency((contract.purchase_price || 0) + (contract.extra_finishing || 0), currency)}
              valueColor={C.primary}
            />
          </div>

          {/* ── Contract meta row ── */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", padding: "0.75rem 1rem", borderRadius: 8, border: `1px solid ${C.border}` }}>
            <MetaChip icon="ri-shield-check-line">
              <span style={{ fontWeight: 600 }}>Type:</span>&nbsp;{contract.type}
            </MetaChip>
            <MetaChip icon="ri-money-euro-circle-line">
              <span style={{ fontWeight: 600 }}>Currency:</span>&nbsp;{currency}
            </MetaChip>
          </div>
        </div>
      ) : (
        <div style={{ padding: "3rem 1.75rem", textAlign: "center", color: C.muted }}>
          <i className="ri-file-unknow-line" style={{ fontSize: "2.5rem", opacity: 0.3 }} />
          <p style={{ marginTop: "0.75rem", fontWeight: 600 }}>No contract — this unit is available.</p>
        </div>
      )}
    </div>
  );
};

export default ContractOwnership;
